import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { ErrorBanner as FormError } from '../components/catalog'
import { Select } from '../components/controls'

export interface Integration {
  integrationId: number
  integration: string
  integrationType: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface IntegrationPage {
  integrations: Integration[]
  total: number
  offset: number
  limit: number
}

/** The values CHK_IntegrationType accepts, in the order the constraint lists them. */
export const INTEGRATION_TYPES = ['games', 'psp', 'crm', 'sportsbook'] as const

const PAGE_SIZE = 50

/**
 * IntegrationsPage is the platform integration catalogue —
 * [Catalog].[Platform.Integration]: the third parties the platform speaks to,
 * from Altenar to Pragmatic to PIQ.
 *
 * The id is chosen, not generated, and seven other tables store it while NOT ONE
 * of them has a foreign key — which is why a retired integration keeps its row
 * and its id, and why the form never offers to change one.
 */
export function IntegrationsPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Integration | null>(null)
  const [formId, setFormId] = useState('')
  const [formName, setFormName] = useState('')
  const [formType, setFormType] = useState('')
  const [error, setError] = useState('')

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(typeFilter ? { type: typeFilter } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['integrations', params],
    queryFn: async (): Promise<IntegrationPage> => {
      const res = await api.get('/integrations', { params })
      return res.data as IntegrationPage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['integrations'] })

  const openCreate = () => {
    setEditing(null)
    setFormId('')
    setFormName('')
    setFormType(typeFilter)
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (i: Integration) => {
    setEditing(i)
    setFormId(String(i.integrationId))
    setFormName(i.integration)
    setFormType(i.integrationType)
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        integration: formName.trim(),
        integrationType: formType,
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/integrations/${editing.integrationId}`, body)
        : api.post('/integrations', { ...body, integrationId: Number(formId) })
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('integrations.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (i: Integration) =>
      i.isDisabledByPlatform
        ? api.post(`/integrations/${i.integrationId}/restore`)
        : api.delete(`/integrations/${i.integrationId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('integrations.errors.save'))),
  })

  const integrations = data?.integrations ?? []
  const total = data?.total ?? 0
  const typeItems = INTEGRATION_TYPES.map((v) => ({ value: v, label: t(`integrations.types.${v}`) }))
  const canSubmit = formName.trim() !== '' && (editing !== null || formId !== '')

  return (
    <div>
      <PageHeading
        title={t('integrations.title')}
        subtitle={t('integrations.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('integrations.newIntegration')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('integrations.editIntegration') : t('integrations.newIntegration')}
                  </DialogTitle>
                </DialogHeader>
                <form
                  className="flex flex-col gap-4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (canSubmit) save.mutate()
                  }}
                >
                  <div className="flex gap-3">
                    <div className="flex w-28 flex-col gap-1.5">
                      <Label htmlFor="integration-id">{t('integrations.columns.id')}</Label>
                      <Input
                        id="integration-id"
                        value={formId}
                        onChange={(e) => setFormId(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                        disabled={editing !== null}
                        placeholder="65"
                        autoFocus={editing === null}
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5">
                      <Label htmlFor="integration-name">{t('integrations.columns.name')}</Label>
                      <Input
                        id="integration-name"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        maxLength={100}
                        placeholder="Pragmatic"
                        autoFocus={editing !== null}
                      />
                    </div>
                  </div>
                  <p className="-mt-2 text-xs text-muted-foreground">{t('integrations.idHint')}</p>

                  <div className="flex w-56 flex-col gap-1.5">
                    <Label htmlFor="integration-type">{t('integrations.columns.type')}</Label>
                    <Select
                      value={formType}
                      onValueChange={setFormType}
                      items={typeItems}
                      placeholder={t('integrations.noType')}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">{t('integrations.typeHint')}</p>
                  </div>

                  <FormError message={error} />

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={save.isPending || !canSubmit}>
                      {editing ? t('common.save') : t('common.create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="integrations">
        <p>{t('integrations.help.line1')}</p>
        <p>{t('integrations.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('integrations.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled || typeFilter !== ''}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setTypeFilter('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      >
        <Select
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v)
            setOffset(0)
          }}
          items={typeItems}
          placeholder={t('integrations.allTypes')}
          className="w-48"
        />
      </CatalogToolbar>

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('integrations.columns.id')}</TableHead>
                  <TableHead>{t('integrations.columns.name')}</TableHead>
                  <TableHead className="w-40">{t('integrations.columns.type')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {integrations.map((i) => (
                  <TableRow key={i.integrationId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {i.integrationId}
                    </TableCell>
                    <TableCell className="font-medium">{i.integration}</TableCell>
                    <TableCell>
                      {i.integrationType ? (
                        <Badge variant="secondary">
                          {t(`integrations.types.${i.integrationType}`, {
                            defaultValue: i.integrationType,
                          })}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={i.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(i)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={i.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(i)}
                          >
                            {i.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {integrations.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('integrations.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('integrations.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
